// ============================================================
// A HIT'S MOMENTS (plan body-map H2; the bench's figure moments, ruled
// 2026-10-09). What a hit can meet before it lands:
//
//   a force field's block   the shield shows at the point of impact, at
//                           once - a patch of hex cells lit from the impact
//                           outward, an impact ring, a flash; an overload
//                           shatters the patch
//   Daemonic protection     the warp swallows the blow - an ink bloom on the
//                           wound, the impact's light streaming into it, the
//                           lines closing over; deeper with X, and the hit
//                           itself lands duller
//
// The geometry, seeded by the hit (never Math.random); the look is CSS.
//
// Pure. Zero React dependency. Frame units; seconds.
// ============================================================

import { seeded } from './hash';
import { sizeStepsAboveAverage } from '../sizes';

import type { Point } from './frame';

/** One cell of the shield's patch: its centre, its size, when it lights. */
export interface FieldCell {
  x: number;
  y: number;
  size: number;
  delay: number;
}

/** How far the patch reaches round the impact, and how long its light takes to spread. */
const PATCH = { reach: 260, spread: 0.2 };

/**
 * The shield's patch round an impact: hex cells within its reach, each lit by its distance from the impact. A
 * stronger field (protection rating, 10-50+) draws smaller, denser cells. `scale`: the plate's (1 on a figure's frame).
 */
export function blockCells([x, y]: Point, rating: number, scale = 1): FieldCell[] {
  const k = Math.min(1, Math.max(0.2, rating / 50));
  const h = (100 - 30 * k) * scale;
  const reach = PATCH.reach * scale;
  const n = Math.ceil(reach / h) + 1;
  const span = Array.from({ length: 2 * n + 1 }, (_, i) => i - n);
  return span
    .flatMap((row) => span.map((col) => hexCentre(x, y, h, row, col)))
    .map(([cx, cy]) => ({ cx, cy, d: Math.hypot(cx - x, cy - y) }))
    .filter(({ d }) => d <= reach)
    .map(({ cx, cy, d }) => ({ x: cx, y: cy, size: h * 0.92, delay: (d / reach) * PATCH.spread }));
}

/** The centre of the hex cell at (row, col) of a grid of height `h` round (x, y): odd rows shifted half a cell. */
function hexCentre(x: number, y: number, h: number, row: number, col: number): [number, number] {
  const w = h * Math.sqrt(3);
  return [x + col * w + (row % 2 ? w / 2 : 0), y + row * h * 1.5];
}

/** A hex cell's points (pointy side up) round its centre. */
export const hexPoints = ({ x, y, size }: Pick<FieldCell, 'x' | 'y' | 'size'>) =>
  Array.from({ length: 6 }, (_, j) => {
    const a = Math.PI / 6 + (j * Math.PI) / 3;
    return `${Math.round(x + size * Math.cos(a))},${Math.round(y + size * Math.sin(a))}`;
  }).join(' ');

/** One streak of the impact's light drawn into the swallow: where it starts, which way in, when. */
export interface Streak {
  x: number;
  y: number;
  /** Its unit direction outward from the wound (it travels the other way). */
  cos: number;
  sin: number;
  /** How far it travels in to the wound. */
  run: number;
  delay: number;
}

/** The swallow at a wound: its reach (deeper with X; times the plate's `scale`) and its streaks of light, seeded. */
export function swallowOf([x, y]: Point, X: number, seed: string, scale = 1): { radius: number; streaks: Streak[] } {
  const radius = (200 + 35 * X) * scale;
  const r = seeded(seed);
  const streaks = Array.from({ length: 14 + 2 * X }, (_, k): Streak => {
    const a = r(`a${k}`) * Math.PI * 2;
    const r0 = radius * (0.9 + 0.6 * r(`r${k}`));
    return {
      x: x + r0 * Math.cos(a),
      y: y + r0 * Math.sin(a),
      cos: Math.cos(a),
      sin: Math.sin(a),
      run: r0,
      delay: 0.08 + 0.3 * r(`d${k}`),
    };
  });
  return { radius, streaks };
}

/** How hard a soaked hit still lands: X more toughness takes the force out of it, down to a quarter. */
export const soakDull = (X: number) => Math.max(0.25, 1 - X / 10);

/** What a hit may meet before it lands, and how big the body is: read off its Target once for every plate. */
export interface Guard {
  /** Its force field's protection rating (the block's patch density). */
  rating: number;
  /** Daemonic protection (X): the soak, the swallow's depth. */
  daemonic: number;
  /** Sizes above Average (Titanbane shakes the ground by it). */
  sizeSteps: number;
  /** How hard a soaked hit still lands (`soakDull`; 1 with no protection). */
  soak: number;
}

type Guarded = {
  forceField?: { protectionRating?: number };
  daemonicProtection?: number;
  size?: string;
};

const guards = new WeakMap<Guarded, Guard>();

export function guardOf(t: Guarded): Guard {
  // One per Target (never mutated): the same object every render, so the layers that take it stay memoised.
  const known = guards.get(t);
  if (known) return known;
  const daemonic = t.daemonicProtection ?? 0;
  const guard = {
    rating: t.forceField?.protectionRating ?? 0,
    daemonic,
    sizeSteps: sizeStepsAboveAverage(t.size),
    soak: soakDull(daemonic),
  };
  guards.set(t, guard);
  return guard;
}
